// Integrations catalog data layer (arc 101 slice 03). Reads `GET /v1/integrations`
// and drives connect / disconnect — the route renders whatever comes back.
// Mirrors `lib/keys.ts`: round-trips over `apiFetch` reusing the dev session
// bearer. Credentials are sealed server-side on arrival; this layer never holds
// plaintext beyond the single POST.

import type { IntegrationKind, IntegrationWire } from '@developerz/domain';
import { IntegrationKindSchema, IntegrationWireSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { API_BASE_URL, apiFetch } from './api';
import { urlOrg } from './org';
import type { Page } from './paged';
import { pageSchema } from './paged-schema';
import { SESSION_TOKEN } from './session';

/**
 * Client-safe integration row — INFERRED from the one wire declaration
 * (`IntegrationResource`, `packages/domain/src/wire/integration.ts`) that
 * `GET /v1/integrations` and the MCP `integration` verb both serialize through.
 * It was a hand-written mirror; nothing mechanical kept it equal to the body,
 * and `apiFetch` is a cast rather than a parse, so a drift rendered `undefined`
 * instead of failing. `Readonly` because this app treats a response as
 * immutable, which the wire schema has no way to say.
 */
export type IntegrationRow = Readonly<IntegrationWire>;

/** Body for `POST /v1/integrations`. The `secret` values are sealed server-side on arrival. */
export type ConnectInput = {
  readonly kind: IntegrationKind;
  readonly config: Record<string, string>;
  readonly secret: Record<string, string>;
};

export const INTEGRATIONS_QUERY_KEY = 'integrations';

/**
 * The catalog: the account's connected integrations + which kinds offer an OAuth
 * connect this deployment (#599).
 *
 * The rows ride the canonical `{data, next_cursor}` envelope, and `next_cursor`
 * is always null here: the route serves the WHOLE catalog — one row per connected
 * kind, a fixed vocabulary — so there is no second page and no Load-more.
 */
export type IntegrationsData = Page<IntegrationRow> & {
  readonly oauth_kinds: readonly IntegrationKind[];
};

/**
 * The catalog body, PARSED (plan 101 slice 15 step 3): the rows through the
 * published resource, and `oauth_kinds` through the SAME `IntegrationKindSchema`
 * the resource's own `kind` field carries — so this envelope narrows nothing the
 * wire has not already published, and a connector added to the domain reaches
 * both halves of the body at once.
 *
 * Adopting `IntegrationWire` as the type made the two declarations one. Parsing
 * is what makes the app FIND OUT when the body stops matching it: until this
 * line the door ended in a cast, so a dropped field rendered as a blank cell
 * nobody can diagnose from a screenshot rather than as this panel's own
 * <ErrorState/> naming the field.
 */
const integrationsDataSchema = pageSchema(IntegrationWireSchema).extend({
  oauth_kinds: z.array(IntegrationKindSchema),
});

/** The account's connected integrations. Disabled (idle) when there's no session, like `useMe`. */
export function useIntegrations(token: string | null = SESSION_TOKEN) {
  return createQuery<IntegrationsData>(() => ({
    queryKey: [INTEGRATIONS_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () =>
      apiFetch('/v1/integrations', token, undefined, { schema: integrationsDataSchema }),
  }));
}

/** Connect (or reconnect) a connector. Returns the secret-free view row. */
export async function connectIntegration(
  input: ConnectInput,
  token: string | null = SESSION_TOKEN,
): Promise<IntegrationRow> {
  return apiFetch(
    '/v1/integrations',
    token,
    { method: 'POST', body: JSON.stringify(input) },
    { schema: IntegrationWireSchema },
  );
}

/** Disconnect a connector the caller owns. Returns the removed view row. */
export async function disconnectIntegration(
  kind: IntegrationKind,
  token: string | null = SESSION_TOKEN,
): Promise<IntegrationRow> {
  return apiFetch(
    `/v1/integrations/${kind}`,
    token,
    { method: 'DELETE' },
    { schema: IntegrationWireSchema },
  );
}

/**
 * The OAuth connect leg's START — `GET /auth/integrations/:kind/login` on the API
 * origin (`apps/api/src/integrations-oauth/flow.ts`). A top-level NAVIGATION
 * target, never a fetch: the connect-tx cookie must be first-party to survive the
 * provider's redirect back, and a relative path would 404 on the dashboard's own
 * origin. One builder, because two screens start this leg — the catalog's
 * "Connect with" button and a blocker's `oauth_connection` field. A kind this
 * deployment has no OAuth client for lands back on the catalog with
 * `?connect_error=unsupported_connector`, which that screen renders by name.
 *
 * `?org=` names the org being VIEWED (`urlOrg`), because a navigation carries
 * no `X-Dz-Account` header: without it the API bound the credential to the
 * session's default account, not the org on screen (plan 101 A2). The API
 * checks the caller's membership of it; this is the intent, not the authority.
 * Outside `/org/:org` there is no viewed org and the session's own stands.
 */
export function integrationOauthLoginUrl(
  kind: IntegrationKind,
  org: string | null = urlOrg(),
): string {
  const base = `${API_BASE_URL}/auth/integrations/${kind}/login`;
  return org === null ? base : `${base}?org=${encodeURIComponent(org)}`;
}
